import {css} from 'aphrodite';
import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import moment from 'moment';
import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {AutomationFileConfigSpec} from 'lab-automation/settings/RunSchemaSpecConstants';
import {nameTemplateModalStyles} from 'name-templates/NameTemplateConstants';
import {makeExampleForField} from 'name-templates/NameTemplateUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import * as Modals from 'util/Modals';
import {assertNever} from 'util/Types';

import {InputFileNamePart, InputFileNamePartType} from './AutomationInputFileConfigConstants';
import InputFileNameTemplate from './InputFileNameTemplate';

export function makeExampleForNameTemplate(
  nameTemplateParts: Array<InputFileNamePart>,
  runSchema: Schema,
  automationFileConfig: AutomationFileConfigSpec
): string {
  return nameTemplateParts.map((part) => makeExampleForPart(part, runSchema, automationFileConfig)).join('');
}

function makeExampleForPart(
  part: InputFileNamePart,
  runSchema: Schema,
  automationFileConfig: AutomationFileConfigSpec
): string {
  switch (part.type) {
    case InputFileNamePartType.CREATED_AT_DATE:
      return moment(new Date()).format('YYYYMMDD').toString();
    case InputFileNamePartType.CREATED_AT_TIME:
      return moment(new Date()).format('HH:mm:ss z').toString();
    case InputFileNamePartType.DISPLAY_NAME:
      return automationFileConfig.name || 'Display name';
    case InputFileNamePartType.FIELD: {
      const field = runSchema.allFields.find((f) => f.apiId === part.fieldId);
      if (field) {
        return makeExampleForField(field);
      } else {
        assertAndContinue(false, 'Expected to find field ID in schemaFields array.');
        return '?';
      }
    }
    case InputFileNamePartType.RUN_API_ID:
      return 'run_QebR9w3N';
    case InputFileNamePartType.RUN_ID:
      return 'a37f1a02-3b95-4646-9946-c658ee3cc4da';
    case InputFileNamePartType.RUN_SCHEMA_NAME:
      return runSchema.name;
    case InputFileNamePartType.SOURCE_FILE_NAME:
      return 'example-file';
    case InputFileNamePartType.TEXT:
      return part.text;
    default:
      assertAndContinue(false, `Unexpected part type: ${assertNever(part).type}`);
      return '?';
  }
}

export interface SetInputFileNameTemplateModalProps {
  runSchema: Schema;
  initialNameTemplateParts: Array<InputFileNamePart>;
  forOutputFileConfig: boolean;
  automationFileConfig: AutomationFileConfigSpec;
  onRequestSubmit: (nameTemplateParts: Array<InputFileNamePart>) => void;
  onRequestClose: () => void;
}

export default function SetInputFileNameTemplateModal(
  props: SetInputFileNameTemplateModalProps
): JSX.Element {
  const [nameTemplateParts, setNameTemplateParts] = useState(props.initialNameTemplateParts);
  const [draggingNameTemplatePartIndex, setDraggingNameTemplatePartIndex] = useState(-1);

  function _getAllOptions(): Array<OptionData> {
    const options = [
      {
        name: 'Run ID',
        value: {type: InputFileNamePartType.RUN_ID},
      },
      {
        name: 'Run API ID',
        value: {type: InputFileNamePartType.RUN_API_ID},
      },
      {
        name: 'Run schema name',
        value: {type: InputFileNamePartType.RUN_SCHEMA_NAME},
      },
      {
        name: 'Display name',
        value: {type: InputFileNamePartType.DISPLAY_NAME},
      },
      {
        name: 'Text',
        value: {type: InputFileNamePartType.TEXT, text: ''},
      },
      ...props.runSchema.fields.map((field: SchemaField) => ({
        name: `Field: ${field.name}`,
        value: {type: InputFileNamePartType.FIELD, fieldId: field.apiId},
      })),
      {
        name: 'Creation date',
        value: {type: InputFileNamePartType.CREATED_AT_DATE},
      },
      {
        name: 'Creation time',
        value: {type: InputFileNamePartType.CREATED_AT_TIME},
      },
    ];
    if (props.forOutputFileConfig) {
      options.push({
        name: 'Source file name',
        value: {type: InputFileNamePartType.SOURCE_FILE_NAME},
      });
    }
    return options;
  }

  function appendPart(part: InputFileNamePart): void {
    setNameTemplateParts([...nameTemplateParts, part]);
  }

  const _handleRemovePart = (index: number): void => {
    setNameTemplateParts(reactUpdate(nameTemplateParts, {$splice: [[index, 1]]}));
  };

  const _handleDragStart = (index: number): void => {
    setDraggingNameTemplatePartIndex(index);
  };

  const _handleDrop = (index: number): void => {
    const nameTemplatePart = nameTemplateParts[draggingNameTemplatePartIndex];
    const reorderedNameTemplateParts = [...nameTemplateParts];
    reorderedNameTemplateParts.splice(draggingNameTemplatePartIndex, 1);
    reorderedNameTemplateParts.splice(index, 0, nameTemplatePart);
    setNameTemplateParts(reorderedNameTemplateParts);
  };

  const _handleSelectItem = async (value: InputFileNamePart): Promise<void> => {
    if (value.type === InputFileNamePartType.TEXT) {
      const text = await legacyRunModal(Modals.PromptModal, {
        enableRedesign: false,
        title: 'Enter a value',
        initialValue: '',
        validate: (s: string) => s.length > 0,
        medium: true,
      });
      if (text) {
        appendPart({type: InputFileNamePartType.TEXT, text});
      }
    } else {
      appendPart(value);
    }
  };

  return (
    <Modal
      title="Set name template"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit(nameTemplateParts);
      }}
      buttons={[
        {
          text: 'Set',
          disabled: !nameTemplateParts.length,
          submit: true,
        },
      ]}
    >
      <div className={classNames('u-small-spacing-bottom', css(nameTemplateModalStyles.helpText))}>
        You can re-order components by dragging them.
      </div>
      <div className={css(nameTemplateModalStyles.partChips)}>
        <InputFileNameTemplate
          draggable={true}
          handleDragStart={_handleDragStart}
          handleDrop={_handleDrop}
          nameTemplateParts={nameTemplateParts}
          schemaFields={props.runSchema.fields}
          onRemovePart={_handleRemovePart}
        />
      </div>
      <div className="u-small-spacing-bottom">
        <SelectDropdown
          displayName="New component..."
          menu={
            <SearchableMenu
              width={Menu.WIDTHS.FULL}
              onSelect={_handleSelectItem}
              optionDatas={_getAllOptions()}
            />
          }
        />
      </div>
      {nameTemplateParts.length > 0 && (
        <div className={classNames('u-overflow-wrap-break-word', css(nameTemplateModalStyles.example))}>
          <span className={css(nameTemplateModalStyles.exampleLabel)}>Preview: </span>
          <span className={css(nameTemplateModalStyles.exampleContent)}>
            {makeExampleForNameTemplate(nameTemplateParts, props.runSchema, props.automationFileConfig)}
          </span>
        </div>
      )}
    </Modal>
  );
}
